import $ from 'jquery';
import debounce from 'debounce';
import layout from '../utils/layout';
import NavDesktopMenu from './NavDesktopMenu';
import NavMobile from './NavMobile';
import MegaNav from './MegaNav';
import LiveSearch from './LiveSearch';

export default class Header {
  constructor(section) {
    this.$el = $(section.el);
    this.el = section.el;
    this.$body = $('body');
    this.$menuToggle = $('.mobile-navigation-toggle', this.$el);
    this.$navigation = $('.navigation', this.$el);
    this.$mainHeader = $('.main-header', this.$el);
    this.$headerWrapper = this.$el.find('.main-header-wrapper');
    this.$announcementBar = $('.pxs-announcement-bar');
    this.$meganavBlocks = {};

    this._bindEvents();
    this.settings = section.data.settings;
    this.currentLayout = this.settings.layout;
    this.navOverflow = false;
    this.navOpen = false;
    this.isFirstLoad = true;

    if (this.settings.sticky || this.settings.layout !== 'traditional') {
      this.events.push($(window).on('resize.header', () => debounce(this._checkHeaderIntersections(), 500)));

      this._checkHeaderIntersections();
    }

    this.handleHeaderStickiness = () => {
      if (this.settings.sticky) {
        this.$body.addClass('main-header-sticky');
        let announcementHeight = 0;
        this.$announcementBar = $('.pxs-announcement-bar');
        if (this.$announcementBar.length) {
          announcementHeight = this.$announcementBar.outerHeight();
        }

        if (window.pageYOffset > announcementHeight) {
          this.$body.removeClass('sticky-announcement-bar');
          this.$headerWrapper.css({ top: 0 });
        } else {
          this.$body.addClass('sticky-announcement-bar');
          this.$headerWrapper.css({ top: announcementHeight });
        }
      } else {
        this.$body.removeClass('main-header-sticky');
        this.$body.removeClass('sticky-announcement-bar');
        this.$headerWrapper.css({ top: '' });
      }
    };

    if (this.settings.sticky) {
      window.addEventListener('scroll', this.handleHeaderStickiness);
    }

    this.handleHeaderStickiness();

    this._setBodyOffset();

    if (this.settings.sticky) {
      this.observer = new IntersectionObserver(entries => this._transformHeaderLayout(entries));
      this.observer.observe(this.el.querySelector('.intersection-target'));
    }

    // Revert navigation to original state on breakpoint change
    this.layoutHandler = this.onBreakpointChange.bind(this);
    layout.onBreakpointChange(this.layoutHandler);

    this.navMobile = new NavMobile({
      $el: this.$el,
      stickyHeader: this.settings.sticky,
    });

    this.navDesktop = new NavDesktopMenu(document.querySelector('[data-header-navigation] > [data-navmenu]'));

    this.megaNavs = {};
    const meganavMenus = document.querySelectorAll('[data-meganav-menu]');
    for (let i = 0; i < meganavMenus.length; i++) {
      const el = meganavMenus[i];
      const id = el.getAttribute('data-meganav-id');
      this.megaNavs[id] = new MegaNav({ el });
    }

    this.liveSearch = new LiveSearch(
      section.el,
      {
        showBorder: this.settings.show_border,
        liveSearchEnabled: this.settings.enable_live_search,
        searchResultsDisplay: this.settings.search_results_display,
      },
    );
  }

  _bindEvents() {
    this.events = [
      $('.mobile-header-search-toggle', this.$el).on('click.header', this.toggleSearch),
      $('.header-search-input', this.$el).on('blur.header', this.toggleSearch),
      this.$menuToggle.on('click.header', (event) => {
        event.preventDefault();
        this.toggleNav();
      }),
    ];
  }

  _unbindEvents() {
    this.events.forEach($el => $el.off('.header'));
  }

  _transformHeaderLayout(entries) {
    if (this.isFirstLoad) {
      this.isFirstLoad = false;
      return;
    }

    if (this.settings.layout === 'minimal') {
      return;
    }

    const scrolledDown = !entries[0].isIntersecting;

    if (this.settings.layout === 'inline' && !this.navOverflow) {
      if (scrolledDown) {
        this.$mainHeader
          .removeClass('header-layout-inline')
          .addClass('header-layout-minimal');

        this.currentLayout = 'minimal';
      } else {
        this.$mainHeader
          .removeClass('header-layout-minimal')
          .addClass('header-layout-inline');

        this.currentLayout = 'inline';
      }
      return;
    }

    // Otherwise we have traditional header, or inline header w/ overflow, which are the same
    if (!this.navOpen) {
      if (scrolledDown) {
        if (this.currentLayout !== 'minimal') {
          this.$navigation.one('animationend', () => {
            this.$navigation.removeClass('animating-out');

            // when we collapse the header we treat it like an overflowed and minimal layout
            this.$mainHeader
              .removeClass(`header-layout-${this.settings.layout}`)
              .addClass('header-layout-minimal')
              .addClass('header-nav-overflow');

            this.navOverflow = true;
            this.currentLayout = 'minimal';
          });
          this.$navigation.addClass('animating-out');
        }
      } else {
        this.$navigation.one('animationend', () => {
          this.$navigation.removeClass('animating-in');
          this._setBodyOffset();
        });

        this.$navigation.addClass('animating-in');

        this.$mainHeader
          .removeClass('header-layout-minimal')
          .addClass(`header-layout-${this.settings.layout}`);

        this.currentLayout = this.settings.layout;

        if (this.settings.layout === 'traditional') {
          this.$mainHeader.removeClass('header-nav-overflow');
          this.navOverflow = false;
        }
      }
    }
  }

  _setBodyOffset() {
    if (!this.settings.sticky) {
      return;
    }

    const $headerWrapper = this.$el.find('.main-header-wrapper');
    const headerHeight = $headerWrapper[0].getBoundingClientRect().height;
    const headerMargin = window.getComputedStyle($headerWrapper[0]).getPropertyValue('margin-bottom');
    let announcementHeight = 0;

    if (this.$announcementBar.length) {
      announcementHeight = this.$announcementBar.outerHeight();
    }

    const total = announcementHeight + parseInt(headerHeight, 0) + parseInt(headerMargin, 0);

    this.$body.css('paddingTop', `${total}px`);
  }

  _unsetBodyOffset() {
    this.$body.css({ paddingTop: '' });
  }

  _checkHeaderIntersections() {
    const processedClass = 'header-nav-processed';
    const previousOverflowState = this.navOverflow;
    this.navOverflow = false;

    if (this.settings.layout === 'minimal') {
      this.navOverflow = true;
      this.$mainHeader.addClass('header-nav-overflow');
      this.$mainHeader.addClass(processedClass);
      return;
    }

    this.$mainHeader.removeClass('header-nav-overflow');

    if (this.currentLayout === 'traditional') {
      this._setBodyOffset();
      return;
    }

    let headerItemSelectors;
    const margin = 16;

    if (this.settings.centerLogo) {
      // :first-child is used to so we get the width of the contents rather than the container
      headerItemSelectors = [
        '[data-header-navigation] > :first-child',
        '[data-header-branding]',
        '[data-header-tools]',
      ];
    } else {
      headerItemSelectors = [
        '[data-header-branding]',
        '[data-header-navigation] > :first-child',
        '[data-header-tools]',
      ];
    }

    const headerItemBounds = headerItemSelectors
      .map(selector => this.el.querySelector(selector).getBoundingClientRect());

    for (let i = 0; i < headerItemBounds.length - 1; i++) {
      if (headerItemBounds[i].right + margin > headerItemBounds[i + 1].left) {
        this.$mainHeader.addClass('header-nav-overflow');
        this.navOverflow = true;
        break;
      }
    }

    if (this.navOverflow !== previousOverflowState) {
      if (this.navOverflow) {
        this.$navigation.one('animationend', () => {
          this.$navigation.removeClass('animating-in');
          this._setBodyOffset();
        });
        this.$navigation.addClass('animating-in');
      } else {
        this._setBodyOffset();
      }
    } else {
      this._setBodyOffset();
    }

    this.$mainHeader.addClass(processedClass);
  }

  onBreakpointChange() {
    // revert to original layout
    this.$mainHeader
      .removeClass('header-layout-traditional')
      .removeClass('header-layout-inline')
      .removeClass('header-layout-minimal')
      .removeClass('header-nav-open')
      .addClass(`header-layout-${this.settings.layout}`);
    this.currentLayout = this.settings.layout;
    this.navOpen = false;
  }

  toggleNav() {
    if (layout.isBreakpoint('L', 'XL')) {
      // If not mobile, then open nav under the title
      if (this.navOpen) {
        // Close the navigation under the site title
        this.$navigation.one('animationend', () => {
          this.$mainHeader
            .removeClass('header-layout-traditional')
            .removeClass('header-layout-inline')
            .removeClass('header-nav-open')
            .addClass('header-layout-minimal');

          this._setBodyOffset();

          this.$navigation.removeClass('animating-out');
        });

        this.$navigation.addClass('animating-out');

        this.currentLayout = 'minimal';
        this.navOpen = false;
      } else if (this.navOverflow && this.currentLayout === 'minimal') {
        // Expand the navigation under the site title
        this.$navigation.one('animationend', () => {
          this.$navigation.removeClass('animating-in');
        });

        this.$mainHeader
          .removeClass('header-layout-minimal')
          .addClass(this.settings.layout === 'traditional' ? 'header-layout-traditional' : 'header-layout-inline')
          .addClass('header-nav-open');

        this._setBodyOffset();

        this.$navigation.addClass('animating-in');

        this.currentLayout = this.settings.layout === 'traditional' ? 'traditional' : 'inline';
        this.navOpen = true;
      }
    } else {
      // If mobile, open mobile nav menu
      this.navMobile.open();
    }
  }

  toggleSearch() {
    if (!(document.documentElement.clientWidth > 1020)) {
      const form = $('.header-search-form', this.$el);

      if (form.hasClass('active')) {
        form.removeClass('active');
      }
      form.addClass('active').find('input').focus();
    }
  }

  onSectionBlockSelect({ id }) {
    this.megaNavs[id].el.classList.add('meganav-editing-block');
    this.megaNavs[id].sizeSubmenuColumn();
  }

  onSectionBlockDeselect({ id }) {
    if (this.megaNavs[id]) {
      this.megaNavs[id].el.classList.remove('meganav-editing-block');
    }
  }

  onSectionUnload() {
    this._unbindEvents();
    this._unsetBodyOffset();
    this.navMobile.unload();
    this.navDesktop.unload();

    window.removeEventListener('scroll', this.handleHeaderStickiness);

    if (this.observer) {
      this.observer.unobserve(this.el.querySelector('.intersection-target'));
    }

    Object.values(this.megaNavs).forEach((meganav) => {
      meganav.unload();
    });

    this.liveSearch.unload();
  }

  onSectionMessage(name) {
    // If the announcement bar is turned on or off, we need to reset the position of the header.
    if (name === 'announcement-load') {
      this.handleHeaderStickiness();
      this._setBodyOffset();
    }
  }
}
